import { Button, PageHeader, Select, Tag, type TagProps } from 'antd'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { REWARD_TYPES, VALIDITY_INTERVAL_LABELS } from '~/constants/domain'
import {
  ProductType,
  useProductSummaryFragment,
  ValidityInterval,
  VehicleRewardType,
} from '~/gql/generated/graphql'

import { PermissionOnly } from '~/components/Session'
import * as routes from '~/constants/routes'

const tagColours: Record<ProductType, TagProps['color']> = {
  [ProductType.payg]: 'green',
  [ProductType.bundle]: 'blue',
  [ProductType.period_pass]: 'purple',
  [ProductType.day_pass]: 'orange',
  [ProductType.subscription_pass]: 'red',
}

export const productTypes: Record<ProductType, string> = {
  [ProductType.period_pass]: 'Period pass',
  [ProductType.payg]: 'PaYR',
  [ProductType.bundle]: 'Bundle',
  [ProductType.day_pass]: 'Day pass',
  [ProductType.subscription_pass]: 'Subscription Pass',
}

export const ProductTypeTag = ({
  productType,
}: {
  productType: ProductType
}) => <Tag color={tagColours[productType]}>{productTypes[productType]}</Tag>

export const ProductTypeSelect = (
  props: React.ComponentProps<typeof Select>,
) => {
  const options = Object.keys(ProductType).map(item => ({
    value: item,
    label: productTypes[item],
  }))
  return <Select options={options} {...props} />
}

export const RewardTypeSelect = (
  props: React.ComponentProps<typeof Select>,
) => {
  const options = Object.keys(VehicleRewardType).map(item => ({
    value: item,
    label: REWARD_TYPES[item],
  }))
  return <Select options={options} {...props} />
}

export const ValidityIntervalSelect = (
  props: React.ComponentProps<typeof Select>,
) => {
  const options = Object.entries(VALIDITY_INTERVAL_LABELS).map(
    ([value, label]) => ({
      value,
      label,
    }),
  )

  return <Select options={options} {...props} />
}

type ProductPageHeaderProps = {
  id: string
  schemeId: string
  goBack?: boolean
  isEditing?: boolean
}
export const ProductPageHeader = ({
  id,
  schemeId,
  goBack,
  isEditing,
}: ProductPageHeaderProps) => {
  // Use data from cache for headline fields to show them while the detail query is loading
  // This fragment is fetched by the list view
  const { data: fragmentData } = useProductSummaryFragment({ id })
  const history = useHistory()
  const location = useLocation()

  const edit = () =>
    history.push(routes.prependScheme(`/products/${id}/edit`, schemeId))

  return (
    <PageHeader
      title={fragmentData.name}
      subTitle={isEditing ? 'Editing' : fragmentData.id}
      tags={
        fragmentData.product_type
          ? [
              <ProductTypeTag
                productType={fragmentData.product_type}
                key={fragmentData.product_type}
              />,
            ]
          : []
      }
      extra={
        isEditing
          ? []
          : [
              <PermissionOnly permission="product.edit">
                <Button type="primary" onClick={edit} key="edit">
                  Edit
                </Button>
              </PermissionOnly>,
            ]
      }
      onBack={goBack ? history.goBack : undefined}
      breadcrumb={{
        routes: [
          { path: '/products', breadcrumbName: 'Products' },
          {
            path: `/products/${id}`,
            breadcrumbName: fragmentData.name ?? '...',
          },
          { path: `/products/${id}/edit`, breadcrumbName: 'Edit' },
        ],
        itemRender(route) {
          const thisRouteWithScheme = routes.prependScheme(route.path, schemeId)
          const thisDepth = thisRouteWithScheme.split('/').length
          const locationDepth = location.pathname.split('/').length

          if (thisDepth > locationDepth) {
            return null
          }

          if (location.pathname === thisRouteWithScheme) {
            return <span>{route.breadcrumbName}</span>
          } else {
            return (
              <Link key={route.path} to={{ pathname: thisRouteWithScheme }}>
                {route.breadcrumbName}
              </Link>
            )
          }
        },
      }}
    />
  )
}
